import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { ImpressumPage, DatenschutzPage, AgbPage } from "@/pages/LegalPages";
import DashboardPage from "@/pages/DashboardPage";
import AccountsPage from "@/pages/AccountsPage";
import CustomersPage from "@/pages/CustomersPage";
import ProjectsPage from "@/pages/ProjectsPage";
import OutgoingInvoicesPage from "@/pages/OutgoingInvoicesPage";
import IncomingInvoicesPage from "@/pages/IncomingInvoicesPage";
import FixedCostsPage from "@/pages/FixedCostsPage";
import VatPage from "@/pages/VatPage";
import AlertsPage from "@/pages/AlertsPage";
import SettingsPage from "@/pages/SettingsPage";
import InboxPage from "@/pages/InboxPage";
import ImapPage from "@/pages/ImapPage";
import ReportsPage from "@/pages/ReportsPage";
import PaymentModelsPage from "@/pages/PaymentModelsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import TeamPage from "@/pages/TeamPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ImportExportPage from "@/pages/ImportExportPage";
import ForecastPage from "@/pages/ForecastPage";
import ConcentrationPage from "@/pages/ConcentrationPage";
import DecisionsPage from "@/pages/DecisionsPage";
import PaymentPrioritiesPage from "@/pages/PaymentPrioritiesPage";
import ExpenseAuditPage from "@/pages/ExpenseAuditPage";
import HiringPage from "@/pages/HiringPage";
import ReservesPage from "@/pages/ReservesPage";
import PlatformTenantsPage from "@/pages/PlatformTenantsPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500" data-testid="boot-loading">
        AgencyCash wird geladen…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AgbPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/konten" element={<Protected><AccountsPage /></Protected>} />
            <Route path="/kunden" element={<Protected><CustomersPage /></Protected>} />
            <Route path="/projekte" element={<Protected><ProjectsPage /></Protected>} />
            <Route path="/ausgangsrechnungen" element={<Protected><OutgoingInvoicesPage /></Protected>} />
            <Route path="/eingangsrechnungen" element={<Protected><IncomingInvoicesPage /></Protected>} />
            <Route path="/fixkosten" element={<Protected><FixedCostsPage /></Protected>} />
            <Route path="/umsatzsteuer" element={<Protected><VatPage /></Protected>} />
            <Route path="/inbox" element={<Protected><InboxPage /></Protected>} />
            <Route path="/imap" element={<Protected><ImapPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/zahlungsmodelle" element={<Protected><PaymentModelsPage /></Protected>} />
            <Route path="/team" element={<Protected><TeamPage /></Protected>} />
            <Route path="/audit" element={<Protected><AuditLogPage /></Protected>} />
            <Route path="/import-export" element={<Protected><ImportExportPage /></Protected>} />
            <Route path="/forecast" element={<Protected><ForecastPage /></Protected>} />
            <Route path="/klumpenrisiko" element={<Protected><ConcentrationPage /></Protected>} />
            <Route path="/entscheidungen" element={<Protected><DecisionsPage /></Protected>} />
            <Route path="/zahlungs-prioritaeten" element={<Protected><PaymentPrioritiesPage /></Protected>} />
            <Route path="/kosten-audit" element={<Protected><ExpenseAuditPage /></Protected>} />
            <Route path="/hiring" element={<Protected><HiringPage /></Protected>} />
            <Route path="/ruecklagen" element={<Protected><ReservesPage /></Protected>} />
            <Route path="/platform/tenants" element={<Protected><PlatformTenantsPage /></Protected>} />
            <Route path="/warnungen" element={<Protected><AlertsPage /></Protected>} />
            <Route path="/einstellungen" element={<Protected><SettingsPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
